import { useState } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useStadtList,
  useCreateStadt,
  useUpdateStadt,
  useDeleteStadt,
} from '../../api/stadt';
import type { Stadt, CreateStadtInput, UpdateStadtInput } from '../../types/stadt';

interface StadtFormData {
  name: string;
  isActive: boolean;
}

export function StadtListPage() {
  const confirm = useConfirm();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState<string | undefined>();
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStadt, setEditingStadt] = useState<Stadt | null>(null);
  const [formData, setFormData] = useState<StadtFormData>({ name: '', isActive: true });

  const { data: cities, isLoading, error } = useStadtList(showInactive ? undefined : true, search);
  const createMutation = useCreateStadt();
  const updateMutation = useUpdateStadt();
  const deleteMutation = useDeleteStadt();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput || undefined);
  };

  const openCreateModal = () => {
    setEditingStadt(null);
    setFormData({ name: '', isActive: true });
    setIsModalOpen(true);
  };

  const openEditModal = (stadt: Stadt) => {
    setEditingStadt(stadt);
    setFormData({ name: stadt.name, isActive: stadt.isActive });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStadt(null);
    setFormData({ name: '', isActive: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      if (editingStadt) {
        const updateData: UpdateStadtInput = {};
        if (formData.name !== editingStadt.name) {
          updateData.name = formData.name.trim();
        }
        if (formData.isActive !== editingStadt.isActive) {
          updateData.isActive = formData.isActive;
        }

        await updateMutation.mutateAsync({ id: editingStadt.id, data: updateData });
        toast.success('Stadt erfolgreich aktualisiert');
      } else {
        const createData: CreateStadtInput = {
          name: formData.name.trim(),
          isActive: formData.isActive,
        };
        await createMutation.mutateAsync(createData);
        toast.success('Stadt erfolgreich erstellt');
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save stadt:', error);
      toast.error(editingStadt ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen');
    }
  };

  const handleDelete = async (stadt: Stadt) => {
    const confirmed = await confirm({
      title: 'Stadt deaktivieren',
      message: `Möchten Sie "${stadt.name}" wirklich deaktivieren? Die Stadt kann nicht mehr für neue Einträge verwendet werden.`,
      confirmLabel: 'Deaktivieren',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(stadt.id);
        toast.success('Stadt erfolgreich deaktiviert');
      } catch (error) {
        console.error('Failed to delete stadt:', error);
        toast.error('Fehler beim Deaktivieren der Stadt');
      }
    }
  };

  const handleReactivate = async (stadt: Stadt) => {
    try {
      await updateMutation.mutateAsync({ id: stadt.id, data: { isActive: true } });
      toast.success('Stadt erfolgreich reaktiviert');
    } catch (error) {
      console.error('Failed to reactivate stadt:', error);
      toast.error('Fehler beim Reaktivieren der Stadt');
    }
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden der Städte</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Städte verwalten</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verwalten Sie die Städte für Bewerber und Bedarfe
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Neue Stadt
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="sm:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Stadtname..."
                  className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Show Inactive Filter */}
            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Inaktive anzeigen
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Laden...</p>
          </div>
        ) : cities?.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Städte gefunden</h3>
            <p className="mt-1 text-sm text-gray-500">
              Erstellen Sie eine neue Stadt oder ändern Sie Ihre Suchkriterien.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Erstellt am
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Aktionen</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cities?.map((stadt) => (
                <tr key={stadt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{stadt.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      stadt.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {stadt.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(stadt.createdAt).toLocaleDateString('de-DE')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(stadt)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Bearbeiten
                      </button>
                      {stadt.isActive ? (
                        <button
                          onClick={() => handleDelete(stadt)}
                          className="text-red-600 hover:text-red-900"
                          disabled={deleteMutation.isPending}
                        >
                          Deaktivieren
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(stadt)}
                          className="text-green-600 hover:text-green-900"
                          disabled={updateMutation.isPending}
                        >
                          Reaktivieren
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeModal}
            />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {editingStadt ? 'Stadt bearbeiten' : 'Neue Stadt erstellen'}
                  </h3>
                  <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="z.B. Köln"
                        autoFocus
                      />
                    </div>

                    {editingStadt && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="isActive" className="ml-2 text-sm text-gray-600">
                          Aktiv
                        </label>
                      </div>
                    )}

                    <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                      <button
                        type="submit"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {(createMutation.isPending || updateMutation.isPending) ? 'Speichern...' : 'Speichern'}
                      </button>
                      <button
                        type="button"
                        onClick={closeModal}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StadtListPage;
